import { Fragment, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { z } from 'zod';
import { APPROVAL_GROUP_BYS, APPROVAL_STATUSES, DECIDE_MANY_MAX, decideApproval } from '@mkcore/approvals/schema';
import { Badge, Button, Checkbox, EmptyState, ErrorState, NativeSelect, PageHeader, Skeleton, Table, TBody, Td, Th, THead, Tr } from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { approvalsQuery } from '@/features/approvals/queries';
import { DecideDialog } from '@/features/approvals/decide-dialog';
import { BulkDecideDialog } from '@/features/approvals/bulk-dialog';
import { ApprovalRate, RiskBadges, kindLabel } from '@/features/approvals/risk';
import { AgentCall, ApproveCallDialog, FormDialog, FormValues, tone, type Row } from '@/features/approvals/request';

const search = z.object({ status: z.enum(APPROVAL_STATUSES).optional(), group: z.enum(APPROVAL_GROUP_BYS).optional() });
export const Route = createFileRoute('/_app/approvals/')({ validateSearch: search, component: Approvals });

/**
 * The approval inbox (CO-0928, CO-0931): pending first; approve at once, reject or revise with a reason. A call of an
 * agent (CO-0930) shows its tool and arguments, can be approved with changed arguments, and keeps what it answered.
 * CO-1394: grouped per agent or kind with the count and the approval rate, the risk per row, and a decision for the
 * chosen rows at once. Approving in bulk skips the rows with a risk; those are approved one by one.
 */
function Approvals() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  const message = useErrorMessage();
  const canApprove = useCan('approvals:approve');
  const status = s.status ?? 'pending';
  const group = s.group;
  const q = useInfiniteQuery(approvalsQuery({ status: [status], limit: 100, ...(group && { groupBy: group }) }));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const groups = q.data?.pages[0]?.groups ?? [];
  const rates = q.data?.pages.flatMap((p) => p.rates ?? []) ?? [];
  const [dialog, setDialog] = useState<{ id: string; decision: 'reject' | 'revise' } | null>(null);
  const [toolCall, setToolCall] = useState<Row | null>(null);
  const [form, setForm] = useState<Row | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<'approve' | 'reject' | 'revise' | null>(null);
  const approve = useActionMutation(decideApproval, { invalidate: [['approval'], ['billing']], success: t('approvals.done.approve') });
  const linkOf = (url: string) => {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  };
  const selectable = canApprove && status === 'pending';
  const pendingIds = rows.filter((r) => r.status === 'pending').map((r) => r.id);
  const picked = pendingIds.filter((id) => chosen.has(id));
  const pickedRows = rows.filter((r) => picked.includes(r.id));
  const risky = pickedRows.filter((r) => r.risks.length).length;
  const toggle = (ids: string[], on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      for (const id of ids) if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const keyOf = (r: Row) => (group === 'requester' ? r.requestedBy : group === 'kind' ? r.kind : '');
  const rateOf = (r: Row) => rates.find((x) => x.requestedBy === r.requestedBy && x.kind === r.kind);
  // Grouped: in the order of the groups (largest first); rows of a group the first page did not name come last.
  const sections = group
    ? [...groups.map((g) => ({ g, rows: rows.filter((r) => keyOf(r) === g.key) })), { g: null, rows: rows.filter((r) => !groups.some((g) => g.key === keyOf(r))) }].filter((x) => x.rows.length)
    : [{ g: null, rows }];
  const cols = selectable ? 6 : 5;
  return (
    <>
      <PageHeader
        title={t('approvals.title')}
        description={t('approvals.description')}
        actions={
          <span className="inline-flex gap-2">
            <NativeSelect value={group ?? ''} aria-label={t('approvals.group.label')} onChange={(e) => void navigate({ search: { ...s, group: (e.target.value || undefined) as typeof group }, replace: true })}>
              <option value="">{t('approvals.group.none')}</option>
              {APPROVAL_GROUP_BYS.map((v) => (
                <option key={v} value={v}>
                  {t(`approvals.group.${v}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect value={status} aria-label={t('common.status')} onChange={(e) => void navigate({ search: { ...s, status: e.target.value as typeof status }, replace: true })}>
              {APPROVAL_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {t(`approvals.status.${v}`)}
                </option>
              ))}
            </NativeSelect>
          </span>
        }
      />
      {selectable && picked.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md bg-surface-alt p-2 text-sm" role="region" aria-label={t('approvals.bulk.label')}>
          <span className="font-bold">{t('approvals.bulk.chosen', { count: picked.length })}</span>
          {picked.length > DECIDE_MANY_MAX && <span className="text-danger">{t('approvals.bulk.tooMany', { max: DECIDE_MANY_MAX })}</span>}
          <Button size="sm" disabled={picked.length > DECIDE_MANY_MAX || risky === picked.length} onClick={() => setBulk('approve')}>
            <Check /> {t('approvals.action.approve')}
          </Button>
          <Button size="sm" variant="secondary" disabled={picked.length > DECIDE_MANY_MAX} onClick={() => setBulk('revise')}>
            {t('approvals.action.revise')}
          </Button>
          <Button size="sm" variant="ghost" disabled={picked.length > DECIDE_MANY_MAX} onClick={() => setBulk('reject')}>
            <X /> {t('approvals.action.reject')}
          </Button>
          {risky > 0 && <span className="text-xs text-fg-3">{t('approvals.bulk.risky', { count: risky })}</span>}
          <Button size="sm" variant="ghost" onClick={() => setChosen(new Set())}>
            {t('approvals.bulk.clear')}
          </Button>
        </div>
      )}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={t('approvals.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              {selectable && (
                <Th className="w-8">
                  <Checkbox aria-label={t('approvals.bulk.all')} checked={picked.length > 0 && picked.length === pendingIds.length ? true : picked.length ? 'indeterminate' : false} onCheckedChange={(v) => toggle(pendingIds, v === true)} />
                </Th>
              )}
              <Th>{t('approvals.what')}</Th>
              <Th className="hidden sm:table-cell">{t('approvals.requestedBy')}</Th>
              <Th className="hidden md:table-cell">{t('common.created')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {sections.map(({ g, rows: part }, i) => (
              <Fragment key={g?.key ?? `rest-${i}`}>
                {group && (
                  <Tr className="bg-surface-alt">
                    {selectable && (
                      <Td>
                        <Checkbox
                          aria-label={t('approvals.group.choose', { name: g ? groupName(g) : t('approvals.group.other') })}
                          checked={part.every((r) => chosen.has(r.id)) ? true : part.some((r) => chosen.has(r.id)) ? 'indeterminate' : false}
                          onCheckedChange={(v) => toggle(part.filter((r) => r.status === 'pending').map((r) => r.id), v === true)}
                        />
                      </Td>
                    )}
                    <Td colSpan={cols - (selectable ? 1 : 0)}>
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <span className="font-bold">{g ? groupName(g) : t('approvals.group.other')}</span>
                        {g?.requestedByKind === 'agent' && <Badge tone="info">{t('approvals.agent')}</Badge>}
                        <span className="text-xs text-fg-3">{t('approvals.group.count', { count: g?.count ?? part.length })}</span>
                        {g && <RiskBadges risks={g.risks} />}
                        {g && <ApprovalRate rate={g} />}
                      </span>
                    </Td>
                  </Tr>
                )}
                {part.map((a) => (
                  <Tr key={a.id}>
                    {selectable && (
                      <Td>{a.status === 'pending' && <Checkbox aria-label={t('approvals.bulk.choose', { title: a.title })} checked={chosen.has(a.id)} onCheckedChange={(v) => toggle([a.id], v === true)} />}</Td>
                    )}
                    <Td>
                      <Link to={linkOf(a.url)} className="font-bold hover:underline">
                        {a.title}
                      </Link>
                      <span className="ml-2 inline-flex flex-wrap items-center gap-1 align-middle">
                        {group !== 'kind' && <span className="text-xs text-fg-3">{kindLabel(t, a.kind)}</span>}
                        <RiskBadges risks={a.risks} />
                      </span>
                      {a.summary && <span className="block text-xs text-fg-3">{a.summary}</span>}
                      {a.comment && <span className="block text-xs text-fg-2">“{a.comment}”</span>}
                      {a.toolCall && <AgentCall approval={a} />}
                      {a.values && <FormValues approval={a} />}
                    </Td>
                    <Td className="hidden sm:table-cell">
                      {a.requestedByName} {a.requestedByKind === 'agent' && <Badge tone="info">{t('approvals.agent')}</Badge>}
                      {a.requestedByKind === 'agent' && (
                        <span className="block">
                          <ApprovalRate rate={rateOf(a)} />
                        </span>
                      )}
                    </Td>
                    <Td className="hidden tabular-nums md:table-cell">{formatDateTime(a.createdAt)}</Td>
                    <Td>
                      <Badge tone={tone[a.status]}>{t(`approvals.status.${a.status}`)}</Badge>
                      {a.decidedByName && (
                        <span className="block text-xs text-fg-3">
                          {a.decidedByName}
                          {a.decidedVia && a.decidedVia !== 'app' && ` · ${t(`approvals.via.${a.decidedVia}`)}`}
                        </span>
                      )}
                    </Td>
                    <Td className="text-right">
                      {a.status === 'pending' && canApprove && (
                        <span className="inline-flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => (a.toolCall ? setToolCall(a) : a.form ? setForm(a) : approve.mutate({ input: { id: a.id, decision: 'approve' } }))}
                            loading={approve.isPending && approve.variables?.input.id === a.id}
                          >
                            <Check /> {t('approvals.action.approve')}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setDialog({ id: a.id, decision: 'revise' })}>
                            {t('approvals.action.revise')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDialog({ id: a.id, decision: 'reject' })} aria-label={t('approvals.action.reject')}>
                            <X />
                          </Button>
                        </span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Fragment>
            ))}
          </TBody>
        </Table>
      )}
      {dialog && <DecideDialog open approvalId={dialog.id} decision={dialog.decision} onClose={() => setDialog(null)} />}
      {toolCall && <ApproveCallDialog approval={toolCall} onClose={() => setToolCall(null)} />}
      {form && <FormDialog approval={form} onClose={() => setForm(null)} />}
      {bulk && (
        <BulkDecideDialog
          ids={picked}
          titles={new Map(pickedRows.map((r) => [r.id, r.title]))}
          decision={bulk}
          onClose={() => {
            setBulk(null);
            setChosen(new Set());
          }}
        />
      )}
    </>
  );

  function groupName(g: (typeof groups)[number]) {
    return g.kind !== null ? kindLabel(t, g.kind) : (g.requestedByName ?? g.key);
  }
}
